/**
 * Analyzer: Baseline Drift (NEW)
 *
 * Compares this session against the user's own rolling baseline.
 * Context is everything — a -$300 session on a trader who averages
 * +$50/session is a 6x-variance event, but on a trader who averages
 * -$250 it's noise. Both deserve different observations.
 *
 * Baseline window: last N non-empty session days (default 30). Looks at
 * total P&L, trade count, and avg R across that window.
 *
 * Requires allTrades + a valid date on the session (so we can window
 * properly against the user's history, not just this session).
 */

import { CATEGORY, SEVERITY, makeObservation } from '../types';
import { withNetPnl, computeR, groupBy, fmt$ } from '../utils';

export const key = 'baselineDrift';

const BASELINE_SESSIONS = 30;      // How many prior sessions to average
const Z_SIGMA_VARIANCE = 2.0;      // |z| > 2σ = notable deviation
const MIN_BASELINE_SAMPLES = 5;    // Need at least 5 prior sessions to compute std

/**
 * Compute population std-dev. Returns 0 for arrays with < 2 elements.
 */
const stddev = (values) => {
  if (!Array.isArray(values) || values.length < 2) return 0;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
};

export const analyze = (ctx) => {
  const {
    session, summary, trades,
    allTrades = [],
    getTradeNetPnL, getAccountById
  } = ctx;
  if (!session || !Array.isArray(trades) || trades.length === 0) return null;
  if (!Array.isArray(allTrades) || allTrades.length < trades.length * 2) return null;

  const sessionDate = session.date || trades[0]?.date;
  if (!sessionDate) return null;

  // Baseline = prior-day sessions, excluding trades in the CURRENT session.
  // Group all prior trades by date to derive per-day P&L samples.
  const prior = allTrades.filter(t => t.date && t.date < sessionDate);
  if (prior.length < 5) return null;

  const priorEnriched = withNetPnl(prior, getTradeNetPnL, getAccountById);
  const byDay = groupBy(priorEnriched.map(e => ({ ...e, date: e.trade.date })), x => x.date);

  // Take the most recent N day-buckets (chronological descending)
  const dayPnls = Array.from(byDay.entries())
    .map(([date, rows]) => ({ date, pnl: rows.reduce((s, x) => s + x.netPnl, 0) }))
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, BASELINE_SESSIONS)
    .map(d => d.pnl);

  if (dayPnls.length < MIN_BASELINE_SAMPLES) return null;

  const baselineMean = dayPnls.reduce((s, v) => s + v, 0) / dayPnls.length;
  const baselineStd = stddev(dayPnls);
  const { total } = summary;

  // z-score of this session vs. the baseline day distribution
  const z = baselineStd > 0 ? (total - baselineMean) / baselineStd : 0;

  // Also consider R-multiple baseline if available
  const { avgR: sessionAvgR } = computeR(trades);
  const { avgR: baselineAvgR } = computeR(prior);

  // Major positive outlier — celebrate but flag as variance
  if (z >= Z_SIGMA_VARIANCE && total > 0) {
    return makeObservation({
      id: `${key}:${session.id}:pos-outlier`,
      analyzer: key,
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.VARIANCE,
      title: `${fmt$(total)} — ${z.toFixed(1)}σ above your baseline`,
      detail: `Baseline day averages ${fmt$(baselineMean)} (σ ${fmt$(baselineStd)}). This session is a real outlier — could be edge, could be variance.`,
      action: 'Capture what was unusual — if it\'s repeatable, it becomes your next A-setup. If not, don\'t anchor expectations to it.',
      evidence: { total, baselineMean, baselineStd, z, samples: dayPnls.length },
      metric: { label: 'Z-score', value: z, unit: 'σ' },
      confidence: 0.8,
      priority: 74
    });
  }

  // Major negative outlier — real damage event
  if (z <= -Z_SIGMA_VARIANCE && total < 0) {
    return makeObservation({
      id: `${key}:${session.id}:neg-outlier`,
      analyzer: key,
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.CRITICAL,
      category: CATEGORY.RISK,
      title: `${fmt$(total)} — ${Math.abs(z).toFixed(1)}σ worse than your baseline`,
      detail: `Baseline day averages ${fmt$(baselineMean)} (σ ${fmt$(baselineStd)}). This is a real draw-down event, not a typical bad day.`,
      action: 'Before next session: force a review — size caps, session cap, and a hard stop-loss for the day.',
      evidence: { total, baselineMean, baselineStd, z, samples: dayPnls.length },
      metric: { label: 'Z-score', value: z, unit: 'σ' },
      confidence: 0.85,
      priority: 92
    });
  }

  // R-multiple drift (only if we have meaningful samples both sides)
  if (sessionAvgR !== null && baselineAvgR !== null && Math.abs(baselineAvgR) > 0.1) {
    const rDelta = sessionAvgR - baselineAvgR;
    if (Math.abs(rDelta) >= 0.75) {
      const better = rDelta > 0;
      return makeObservation({
        id: `${key}:${session.id}:r-drift`,
        analyzer: key,
        scope: 'session',
        scopeId: session.id,
        severity: better ? SEVERITY.SUCCESS : SEVERITY.WARNING,
        category: CATEGORY.RISK,
        title: `Avg R drifted ${better ? 'up' : 'down'} vs baseline`,
        detail: `Session R ${sessionAvgR.toFixed(2)} vs baseline ${baselineAvgR.toFixed(2)} (Δ ${rDelta > 0 ? '+' : ''}${rDelta.toFixed(2)}).`,
        action: better
          ? 'Check what made R bigger this session — trim discipline, target selection, or trade selection?'
          : 'R compression usually means stops got wider or targets got cut — check your exit rules.',
        evidence: { sessionAvgR, baselineAvgR, rDelta },
        metric: { label: 'R Δ', value: rDelta, baseline: baselineAvgR, unit: 'R' },
        confidence: 0.68,
        priority: better ? 55 : 68
      });
    }
  }

  return null;
};
